import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { IUserShort } from '@spa/data/entities';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
	IAddUnrequiredSignatures,
	IGetDynamicSignatureResult,
	IGetDynamicSignaturesByUserResult,
	IRequestDynamicSignature,
	IRequestDynamicUserSignature,
} from '.';
import { IApiResponse } from '../api-response';
import { ApiVersion } from '../api-versions';
import { DataHttpCommonService } from '../data-http-common.service';
import type { IEndpointUrlConfig } from '../endpoint.config';
import { EndpointUrlConfig } from '../endpoint.config';
import {
	SignaturesHttpService,
	ISignatureDueDateChangeCriteria,
	IAddQuickReplyCriteria,
	IRevokeTaskSignaturesParams,
} from './abstract';

@Injectable()
export class SignaturesHttpServiceImpl implements SignaturesHttpService {
	constructor(
		@Inject(EndpointUrlConfig) public readonly config: IEndpointUrlConfig,
		public readonly http: HttpClient,
		public readonly common: DataHttpCommonService
	) {}

	getQuickReplies(taskId: number | string): Observable<string[]> {
		const url = this.common.getApiUrl(`users/current/quickreplies?withDueReasonForTaskId=${taskId}`, ApiVersion.v10);
		return this.http.get<any>(url).pipe(
			map(result => result),
			catchError(err => {
				console.error(err);
				return of(null);
			})
		);
	}

	changeSignatureDueDate(obj: ISignatureDueDateChangeCriteria): Observable<any> {
		const url = `/TCWeb.asmx/ChangeSignatureDue`;
		return this.http.post(url, obj);
	}
	addQuickReply(obj: IAddQuickReplyCriteria) {
		const url = `/TCWeb.asmx/ReasonDialog_AddQucikReply`;
		return this.http.post(url, obj);
	}

	getDynamicSignatures(taskId: number): Observable<IGetDynamicSignatureResult> {
		const url = `/TCWeb.asmx/GetDynamicSignatures`;
		return this.http.post<IGetDynamicSignatureResult>(url, { taskId });
	}
	getDynamicSignaturesByUser(taskId: number, userId: string): Observable<IGetDynamicSignaturesByUserResult> {
		const url = `/TCWeb.asmx/GetDynamicSignaturesByUser`;
		return this.http.post<IGetDynamicSignaturesByUserResult>(url, { taskId, userId });
	}
	requestDynamicUserSignature(obj: IRequestDynamicUserSignature): Observable<any> {
		const url = `/TCWeb.asmx/RequestDynamicUserSignature`;
		return this.http.post(url, obj);
	}
	requestDynamicSignature(obj: IRequestDynamicSignature): Observable<any> {
		const url = `/TCWeb.asmx/RequestDynamicSignature`;
		return this.http.post(url, obj);
	}

	getPossibleAcceptants(taskId: number, signatureId: number): Observable<IUserShort[]> {
		const url = this.common.getApiUrl(`tasks/signatures/possible-acceptants/${taskId}/${signatureId}`);
		return this.http.get<IUserShort[]>(url);
	}

	getSignaturesById(id: number): Observable<any> {
		const url = this.common.getApiUrl(`storage/signatures/${id}`);
		return this.http.get<any>(url);
	}

	revokeTaskSignatures(params: IRevokeTaskSignaturesParams): Observable<any> {
		const url = this.common.getEndpointUrl(`/TCWeb.asmx/RevokeTaskSignatures`);
		params.location = params.location || null;
		return this.http.post(url, params);
	}

	addUnrequiredSignatures(obj: IAddUnrequiredSignatures) {
		const url = this.common.getEndpointUrl(`/TCWeb.asmx/AddUnrequiredSignatures`);
		return this.http.post(url, obj);
	}

	changeTaskStepWithoutUnrequiredSignatures(taskId: number) {
		const url = this.common.getEndpointUrl(`/TCWeb.asmx/ChangeTaskStepWithoutUnrequiredSignatures`);
		const body = { taskId: taskId };
		return this.http.post(url, body);
	}
}
